import { useEffect } from "react";
import { useT } from "@/i18n/TranslationContext";
import ContactForm from "@/components/ContactForm";
import {
  BarChart3,
  Layers,
  Globe2,
  Sparkles,
  ArrowRight,
  LineChart,
  Gauge,
  ScrollText,
  MapPin,
  Zap,
  Mountain,
  Droplets,
  Factory,
  ShieldCheck,
  ClipboardCheck,
  Search,
  Wallet,
} from "lucide-react";

const StatCard = ({ label, value, desc, accent = "#0b2545" }) => (
  <div className="relative overflow-hidden rounded-2xl border border-[#0b2545]/10 bg-white p-5 sm:p-6 shadow-sm hover:shadow-md transition">
    <span
      className="absolute top-0 left-0 h-full w-1"
      style={{ background: accent }}
      aria-hidden
    />
    <p className="text-xs uppercase tracking-widest text-[#0b2545]/60 font-semibold">{label}</p>
    <p
      className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight"
      style={{ color: accent }}
    >
      {value}
    </p>
    <p className="mt-2 text-sm text-[#0b2545]/70 leading-relaxed">{desc}</p>
  </div>
);

const SectionHeading = ({ eyebrow, title, lead, align = "left" }) => (
  <div className={`max-w-3xl ${align === "center" ? "mx-auto text-center" : ""}`}>
    <p className="text-xs uppercase tracking-[0.2em] text-[#c46a3d] font-bold">{eyebrow}</p>
    <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-[#0b2545]">
      {title}
    </h2>
    {lead && <p className="mt-4 text-base sm:text-lg text-[#0b2545]/75 leading-relaxed">{lead}</p>}
  </div>
);

const Home = () => {
  const { t, lang, dict } = useT();
  const base = `/${lang}`;

  useEffect(() => {
    document.title = t("meta.title");
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", t("meta.description"));
    document.documentElement.setAttribute("lang", lang === "ar" ? "es-AR" : "en");
  }, [t, lang]);

  const whyIcons = [Search, Globe2, Layers, Sparkles];
  const stepIcons = [ScrollText, Wallet, Gauge, ClipboardCheck];

  const sectors = [
    { key: "lithium", Icon: Droplets, accent: "#c46a3d", img: "https://images.pexels.com/photos/20888168/pexels-photo-20888168.jpeg?w=1400&auto=compress&cs=tinysrgb" },
    { key: "oilgas", Icon: Zap, accent: "#13315c", img: "https://images.pexels.com/photos/16862261/pexels-photo-16862261.jpeg?w=1400&auto=compress&cs=tinysrgb" },
    { key: "mining", Icon: Mountain, accent: "#7b3f2a", img: "https://images.pexels.com/photos/33074372/pexels-photo-33074372.jpeg?w=1400&auto=compress&cs=tinysrgb" },
    { key: "infra", Icon: Factory, accent: "#3b5d84", img: "https://images.pexels.com/photos/35224901/pexels-photo-35224901.jpeg?w=1400&auto=compress&cs=tinysrgb" },
  ];

  return (
    <main className="ecominarg-main">
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#f6f4ef] via-[#f8f7f3] to-white">
        <div
          className="absolute inset-0 opacity-[0.15] pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, rgba(11,37,69,0.35), transparent 45%), radial-gradient(circle at 80% 60%, rgba(196,106,61,0.35), transparent 40%)",
          }}
          aria-hidden
        />
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.06]"
          style={{
            backgroundImage:
              "linear-gradient(#0b2545 1px, transparent 1px), linear-gradient(90deg, #0b2545 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
          aria-hidden
        />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-16 sm:pt-24 pb-16 lg:pb-24 grid gap-12 lg:grid-cols-12 items-center">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 rounded-full border border-[#0b2545]/15 bg-white/70 px-3 py-1.5 text-xs font-semibold text-[#0b2545]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#c46a3d]" />
              {t("hero.eyebrow")}
            </span>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#0b2545] leading-[1.05]">
              {t("hero.title")}
            </h1>
            <p className="mt-3 text-lg sm:text-xl font-semibold text-[#c46a3d]">
              {t("hero.subtitle")}
            </p>
            <p className="mt-6 text-base sm:text-lg text-[#0b2545]/78 leading-relaxed max-w-2xl">
              {t("hero.lead")}
            </p>
            <p className="mt-4 text-base sm:text-lg text-[#0b2545]/78 leading-relaxed max-w-2xl">
              {t("hero.lead_2")}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href={`${base}#contact`}
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#0b2545] text-white font-semibold text-sm hover:bg-[#13315c] transition shadow-lg shadow-[#0b2545]/15"
                data-testid="hero-cta-primary"
              >
                {t("hero.cta_primary")} <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href={`${base}#sectors`}
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full border border-[#0b2545]/25 text-[#0b2545] font-semibold text-sm hover:bg-[#0b2545]/5 transition"
                data-testid="hero-cta-secondary"
              >
                {t("hero.cta_secondary")}
              </a>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative rounded-3xl overflow-hidden border border-[#0b2545]/10 shadow-2xl">
              <img
                src="https://images.pexels.com/photos/37899352/pexels-photo-37899352.jpeg?w=1600&auto=compress&cs=tinysrgb"
                alt={"Salinas Grandes en Jujuy \u2014 Tri\u00e1ngulo del Litio, Argentina"}
                className="w-full h-[380px] sm:h-[460px] object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0b2545]/85 via-[#0b2545]/20 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#e9c46a] font-bold">
                  <MapPin className="w-3.5 h-3.5" /> Salar de Olaroz · Jujuy
                </div>
                <p className="mt-2 text-white text-base sm:text-lg font-semibold leading-snug max-w-md">
                  {lang === "ar"
                    ? "Monitoreo trimestral de curvas de extracci\u00f3n, agua y ley promedio."
                    : "Quarterly monitoring of extraction curves, water and average grade."}
                </p>
              </div>
              <div className="absolute top-4 left-4 rounded-xl bg-white/95 backdrop-blur px-3 py-2 shadow">
                <p className="text-[10px] uppercase tracking-widest text-[#0b2545]/60 font-bold">Li₂CO₃</p>
                <p className="text-lg font-extrabold text-[#0b2545]">US$ 18,240 / t</p>
                <p className="text-[10px] text-emerald-700 font-semibold">+3.4% QoQ</p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats bar */}
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pb-14">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard label={t("stats.lithium_title")} value={t("stats.lithium_value")} desc={t("stats.lithium_desc")} accent="#c46a3d" />
            <StatCard label={t("stats.vaca_title")} value={t("stats.vaca_value")} desc={t("stats.vaca_desc")} accent="#0b2545" />
            <StatCard label={t("stats.projects_title")} value={t("stats.projects_value")} desc={t("stats.projects_desc")} accent="#3b5d84" />
            <StatCard label={t("stats.reports_title")} value={t("stats.reports_value")} desc={t("stats.reports_desc")} accent="#7b3f2a" />
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28">
        <SectionHeading eyebrow={t("why.eyebrow")} title={t("why.title")} />
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {(dict.why?.items || []).map((it, idx) => {
            const Icon = whyIcons[idx] || Sparkles;
            return (
              <div
                key={idx}
                className="group relative rounded-2xl bg-white p-6 border border-[#0b2545]/10 hover:border-[#0b2545]/30 transition shadow-sm hover:shadow-lg"
              >
                <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#0b2545] text-white group-hover:bg-[#c46a3d] transition">
                  <Icon className="w-5 h-5" />
                </span>
                <h3 className="mt-4 text-lg font-bold text-[#0b2545] leading-snug">{it.title}</h3>
                <p className="mt-2 text-sm text-[#0b2545]/72 leading-relaxed">{it.desc}</p>
                <span className="absolute top-6 right-6 text-xs font-bold text-[#0b2545]/25">0{idx + 1}</span>
              </div>
            );
          })}
        </div>
      </section>

      {/* SECTORS */}
      <section id="sectors" className="bg-[#f6f4ef] py-20 lg:py-28 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeading eyebrow={t("sectors.eyebrow")} title={t("sectors.title")} />
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {sectors.map(({ key, Icon, accent, img }) => {
              const s = t(`sectors.${key}`);
              return (
                <article
                  key={key}
                  className="group relative overflow-hidden rounded-3xl bg-white border border-[#0b2545]/10 shadow-sm hover:shadow-xl transition"
                >
                  <div className="relative h-48 sm:h-56 overflow-hidden">
                    <img
                      src={img}
                      alt={s.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
                    <span
                      className="absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-full bg-white/95 backdrop-blur px-3 py-1 text-[11px] font-bold uppercase tracking-widest"
                      style={{ color: accent }}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      {s.tag}
                    </span>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-[#0b2545]">{s.title}</h3>
                    <p className="mt-2 text-sm text-[#0b2545]/72 leading-relaxed">{s.desc}</p>
                    <a
                      href={`mailto:${t("footer.email")}?subject=${encodeURIComponent(s.title)}`}
                      className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0b2545] hover:text-[#c46a3d]"
                    >
                      {t("sectors.cta")} <ArrowRight className="w-4 h-4" />
                    </a>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section id="process" className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28 scroll-mt-24">
        <SectionHeading eyebrow={t("process.eyebrow")} title={t("process.title")} lead={t("process.lead")} />
        <div className="mt-12 grid gap-6 lg:grid-cols-4">
          {(dict.process?.steps || []).map((s, idx) => {
            const Icon = stepIcons[idx] || BarChart3;
            return (
              <div key={idx} className="relative rounded-2xl border border-[#0b2545]/10 bg-white p-6 shadow-sm hover:shadow-md transition">
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[#0b2545]/5 text-[#0b2545] border border-[#0b2545]/10">
                    <Icon className="w-5 h-5" />
                  </span>
                  <span className="text-3xl font-extrabold text-[#c46a3d]">0{idx + 1}</span>
                </div>
                <h3 className="mt-4 text-lg font-bold text-[#0b2545] leading-snug">{s.title}</h3>
                <p className="mt-2 text-sm text-[#0b2545]/72 leading-relaxed">{s.desc}</p>
                {idx < (dict.process?.steps || []).length - 1 && (
                  <span className="hidden lg:block absolute right-[-14px] top-1/2 -translate-y-1/2 text-[#0b2545]/25">
                    <ArrowRight className="w-6 h-6" />
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* TRANSPARENCY */}
      <section id="transparency" className="relative py-20 lg:py-28 bg-[#0b2545] text-white overflow-hidden scroll-mt-24">
        <div
          className="absolute inset-0 opacity-[0.09] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(#e9c46a 1px, transparent 1px), linear-gradient(90deg, #e9c46a 1px, transparent 1px)",
            backgroundSize: "36px 36px",
          }}
          aria-hidden
        />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid gap-12 lg:grid-cols-12 items-center">
          <div className="lg:col-span-6">
            <p className="text-xs uppercase tracking-[0.2em] text-[#e9c46a] font-bold">{t("transparency.eyebrow")}</p>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white">
              {t("transparency.title")}
            </h2>
            <p className="mt-5 text-white/80 leading-relaxed">{t("transparency.body")}</p>
            <p className="mt-4 text-white/80 leading-relaxed">{t("transparency.body_2")}</p>

            <div className="mt-8 grid gap-4 sm:grid-cols-3">
              {(dict.transparency?.pillars || []).map((p, idx) => (
                <div key={idx} className="rounded-xl bg-white/5 border border-white/10 p-4">
                  <div className="flex items-center gap-2 text-[#e9c46a]">
                    <ShieldCheck className="w-4 h-4" />
                    <span className="text-xs font-bold uppercase tracking-widest">0{idx + 1}</span>
                  </div>
                  <h3 className="mt-2 text-white font-semibold text-sm">{p.title}</h3>
                  <p className="mt-1 text-white/70 text-xs leading-relaxed">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl">
              <img
                src="https://images.pexels.com/photos/36755611/pexels-photo-36755611.jpeg?w=1600&auto=compress&cs=tinysrgb"
                alt={"Dashboard anal\u00edtico de commodities y macroeconom\u00eda argentina"}
                className="w-full h-[380px] sm:h-[460px] object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0b2545]/85 via-transparent" />
              <div className="absolute top-4 left-4 rounded-xl bg-white/95 backdrop-blur px-3 py-2 shadow flex items-center gap-2">
                <LineChart className="w-4 h-4 text-[#0b2545]" />
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-[#0b2545]/60 font-bold">Brent · WTI · GNL</p>
                  <p className="text-sm font-extrabold text-[#0b2545]">Panel de commodities</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="relative py-20 lg:py-28 bg-gradient-to-b from-white to-[#f6f4ef] scroll-mt-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow={t("contact.eyebrow")}
              title={t("contact.title")}
              lead={t("contact.lead")}
            />
            <div className="mt-8 space-y-4 text-sm text-[#0b2545]/80">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#c46a3d] mt-0.5" />
                <div>
                  <p className="font-semibold text-[#0b2545]">{t("footer.company")}</p>
                  <p className="leading-relaxed">{t("footer.address")}</p>
                </div>
              </div>
              <p>
                <a href={`mailto:${t("footer.email")}`} className="font-semibold text-[#0b2545] hover:text-[#c46a3d]">
                  {t("footer.email")}
                </a>
              </p>
              <p>
                <a href={`tel:${t("footer.phone").replace(/\s/g, "")}`} className="font-semibold text-[#0b2545] hover:text-[#c46a3d]">
                  {t("footer.phone")}
                </a>
              </p>
            </div>
          </div>
          <div className="lg:col-span-7">
            <ContactForm />
          </div>
        </div>
      </section>
    </main>
  );
};

export default Home;
